import { Box, Typography, InputBase, IconButton } from "@mui/material";
import { useMemo } from "react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { uiNeutrals } from "../../../../constants/uiNeutrals";
import {
  closeIconButtonDenseSx,
  closeIconButtonSx,
} from "../../../../constants/closeIconButtonSx";
import {
  ALL_STATES,
  STATE_TO_AREAS,
} from "../constants/malaysiaLocations";

const AREA_TO_STATE = {};
Object.entries(STATE_TO_AREAS).forEach(([state, areas]) => {
  areas.forEach((area) => {
    AREA_TO_STATE[area] = state;
  });
});

/**
 * Shared state → area browser used by filter Area row and Telegram reminders.
 * Matches Filter Page location picker visuals (surface search row).
 *
 * @param {"single"|"multi"} props.variant — single picks one area then closes via callback
 */
const AreaBrowsePickerDialog = ({
  open,
  onClose,
  step,
  browsingState,
  query,
  onQueryChange,
  onBack,
  onSelectState,
  variant = "single",
  selectedSingleArea = null,
  onPickSingleArea,
  selectedAreasMulti = [],
  onToggleAreaMulti,
  maxSelections = 3,
}) => {
  const filteredStates = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALL_STATES;
    return ALL_STATES.filter((s) => s.toLowerCase().includes(q));
  }, [query]);

  const filteredAreas = useMemo(() => {
    const areas = [...(STATE_TO_AREAS[browsingState] || [])].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
    const q = query.trim().toLowerCase();
    if (!q) return areas;
    return areas.filter((a) => a.toLowerCase().includes(q));
  }, [browsingState, query]);

  const derivedSingleState =
    variant === "single" && selectedSingleArea
      ? AREA_TO_STATE[selectedSingleArea]
      : null;

  const statesWithMultiSelection = useMemo(() => {
    if (variant !== "multi") return null;
    const s = new Set();
    for (const a of selectedAreasMulti) {
      const st = AREA_TO_STATE[a];
      if (st) s.add(st);
    }
    return s;
  }, [variant, selectedAreasMulti]);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        bgcolor: "rgba(255, 255, 255, 0.5)",
        backdropFilter: "blur(2px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
      }}
      onClick={onClose}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: "100%",
          maxWidth: 340,
          height: "65vh",
          bgcolor: "background.default",
          borderRadius: "16px",
          border: "1px solid #ddd",
          boxShadow: "none",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {step === "area" && (
            <IconButton size="small" onClick={onBack} sx={{ ml: -0.5 }}>
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <Typography sx={{ fontSize: "16px", fontWeight: 600, flex: 1 }}>
            {step === "state" ? "Select State" : browsingState}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={closeIconButtonSx}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 2, py: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 1,
              borderRadius: "12px",
              bgcolor: uiNeutrals.surface,
            }}
          >
            <SearchIcon
              sx={{ color: "#9ca3af", fontSize: 18, flexShrink: 0 }}
            />
            <InputBase
              fullWidth
              autoFocus={false}
              placeholder={
                step === "state"
                  ? "Search state…"
                  : `Search in ${browsingState}…`
              }
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              sx={{ fontSize: "16px", flex: 1 }}
              inputProps={{
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: "false",
              }}
            />
            {query ? (
              <IconButton
                size="small"
                onClick={() => onQueryChange("")}
                aria-label="Clear search"
                sx={closeIconButtonDenseSx}
              >
                <CloseIcon />
              </IconButton>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            touchAction: "pan-y",
          }}
        >
          {step === "state" ? (
            filteredStates.length === 0 ? (
              <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No states found
                </Typography>
              </Box>
            ) : (
              filteredStates.map((state) => {
                const isStateHighlighted =
                  variant === "single"
                    ? derivedSingleState === state
                    : Boolean(statesWithMultiSelection?.has(state));
                return (
                  <Box
                    key={state}
                    onClick={() => onSelectState(state)}
                    sx={{
                      px: 3,
                      py: 1.5,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      bgcolor: isStateHighlighted
                        ? uiNeutrals.surface
                        : "transparent",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: isStateHighlighted ? 600 : 400,
                        color: "#000",
                      }}
                    >
                      {state}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {isStateHighlighted ? (
                        <CheckIcon sx={{ fontSize: 16, color: "#000" }} />
                      ) : null}
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          flexShrink: 0,
                          border: `1px solid ${uiNeutrals.wellBorder}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#fff",
                        }}
                      >
                        <KeyboardArrowDownIcon
                          sx={{
                            fontSize: 18,
                            color: "#9ca3af",
                            transform: "rotate(-90deg)",
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )
          ) : filteredAreas.length === 0 ? (
            <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No areas found
              </Typography>
            </Box>
          ) : (
            filteredAreas.map((area) => {
              const isSelected =
                variant === "single"
                  ? selectedSingleArea === area
                  : selectedAreasMulti.includes(area);
              const isDisabled =
                variant === "multi" &&
                !isSelected &&
                selectedAreasMulti.length >= maxSelections;

              const handleAreaClick = () => {
                if (variant === "single") {
                  onPickSingleArea(area);
                } else if (!isDisabled) {
                  onToggleAreaMulti(area);
                }
              };

              return (
                <Box
                  key={area}
                  onClick={handleAreaClick}
                  sx={{
                    px: 3,
                    py: 1.5,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    opacity: isDisabled ? 0.4 : 1,
                    bgcolor: isSelected ? uiNeutrals.surface : "transparent",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: isSelected ? 600 : 400,
                    }}
                  >
                    {area}
                  </Typography>
                  {isSelected ? (
                    <CheckIcon sx={{ fontSize: 16, color: "#000" }} />
                  ) : null}
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AreaBrowsePickerDialog;
